import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Loader2, Download, FileText, Globe } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const REMEMBER_KEY = "pdf_download_prefs_v1";

/**
 * Reusable download dialog: pick PDF format + language, remember choice.
 * Works for both sync (`GET /report-pdf?brand=&lang=`) and two-step
 * (`POST /report-pdf-prepare` → `GET /report-pdf-download/{key}`) flows.
 *
 * Props:
 *   open / onOpenChange — controlled
 *   inspectionId  (string, required)
 *   context       ("b2c" | "dealer" | "partner" | "admin") — filters formats
 *   defaultFormat ("wisedrive"|"sheerdrive"|…)  — initial pick
 *   twoStep       (bool, default true) — use prepare→download flow for Safari
 *   onError / onSuccess   (optional callbacks)
 */
export default function DownloadReportDialog({
  open,
  onOpenChange,
  inspectionId,
  context = "admin",
  defaultFormat = "wisedrive",
  twoStep = true,
  onError,
  onSuccess,
}) {
  const [formats, setFormats] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [format, setFormat] = useState(defaultFormat);
  const [lang, setLang] = useState("en");
  const [remember, setRemember] = useState(true);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Load remembered choice on open
  useEffect(() => {
    if (!open) return;
    try {
      const saved = JSON.parse(localStorage.getItem(REMEMBER_KEY) || "{}");
      if (saved.format) setFormat(saved.format);
      if (saved.lang) setLang(saved.lang);
    } catch (_) { /* ignore */ }
  }, [open]);

  // Load formats + languages when dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [f, l] = await Promise.all([
          axios.get(`${API}/api/report-formats`, { params: { context }, headers }),
          axios.get(`${API}/api/languages/active`, { headers }),
        ]);
        if (cancelled) return;
        setFormats(f.data || []);
        setLanguages(l.data || []);
        // Ensure format is valid; else pick default from API
        if (f.data?.length) {
          const ok = f.data.find((x) => x.code === format);
          if (!ok) {
            const def = f.data.find((x) => x.is_default) || f.data[0];
            setFormat(def.code);
          }
        }
      } catch (e) {
        console.error("[DownloadDialog] meta load failed", e);
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, context]);

  const selectedLang = useMemo(
    () => languages.find((x) => x.code === lang) || { code: "en", name: "English", native_name: "English" },
    [languages, lang],
  );

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) throw new Error("Not authenticated");

      if (remember) {
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({ format, lang }));
      }

      if (twoStep) {
        // Phase 1: pre-generate on server
        const prep = await axios.post(
          `${API}/api/inspections/${inspectionId}/report-pdf-prepare`,
          null,
          { params: { brand: format, lang }, headers: { Authorization: `Bearer ${token}` } },
        );
        const { cache_key, filename } = prep.data || {};
        if (!cache_key) throw new Error("No cache_key returned");
        // Phase 2: stream the PDF bytes
        const pdfRes = await fetch(
          `${API}/api/report-pdf-download/${cache_key}?token=${encodeURIComponent(token)}`,
        );
        if (!pdfRes.ok) throw new Error("Download failed");
        const blob = await pdfRes.blob();
        triggerBrowserDownload(blob, filename || `report_${format}_${lang}.pdf`);
      } else {
        // Single-shot download
        const res = await fetch(
          `${API}/api/inspections/${inspectionId}/report-pdf?brand=${format}&lang=${lang}&token=${encodeURIComponent(token)}`,
        );
        if (!res.ok) throw new Error("Download failed");
        const blob = await res.blob();
        triggerBrowserDownload(blob, `report_${format}_${lang}.pdf`);
      }

      onSuccess?.();
      onOpenChange?.(false);
    } catch (err) {
      console.error("[DownloadDialog] download failed:", err);
      onError?.(err);
      alert(err?.message || "Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const triggerBrowserDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="download-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Download Report
          </DialogTitle>
          <DialogDescription>
            Choose the report format and language. Your preference will be remembered next time.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Format */}
          <div>
            <Label className="flex items-center gap-2 mb-2 text-sm font-semibold">
              <FileText className="h-4 w-4" /> Report Format
            </Label>
            {loadingMeta ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading formats…
              </div>
            ) : (
              <RadioGroup value={format} onValueChange={setFormat} className="space-y-2">
                {formats.map((f) => (
                  <div
                    key={f.code}
                    className="flex items-start gap-3 rounded-lg border p-3 hover:bg-muted/50 transition"
                  >
                    <RadioGroupItem
                      value={f.code}
                      id={`fmt-${f.code}`}
                      data-testid={`download-format-${f.code}`}
                    />
                    <div className="flex-1">
                      <label htmlFor={`fmt-${f.code}`} className="font-medium cursor-pointer">
                        {f.label}
                      </label>
                      {f.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{f.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </RadioGroup>
            )}
          </div>

          {/* Language */}
          <div>
            <Label className="flex items-center gap-2 mb-2 text-sm font-semibold">
              <Globe className="h-4 w-4" /> Language
            </Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="download-lang-trigger">
                <SelectValue>
                  {selectedLang?.name}{" "}
                  {selectedLang?.native_name && selectedLang.native_name !== selectedLang.name && (
                    <span className="text-muted-foreground">({selectedLang.native_name})</span>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {languages.map((l) => (
                  <SelectItem
                    key={l.code}
                    value={l.code}
                    data-testid={`download-lang-${l.code}`}
                  >
                    {l.name}
                    {l.native_name && l.native_name !== l.name && (
                      <span className="text-muted-foreground ml-2">({l.native_name})</span>
                    )}
                    {l.is_default && (
                      <span className="ml-2 text-xs text-emerald-600 font-medium">default</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {lang !== "en" && (
              <p className="text-xs text-muted-foreground mt-1.5">
                First download in a new language may take 30–60 seconds while the report is translated.
                Subsequent downloads are instant.
              </p>
            )}
          </div>

          {/* Remember */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="remember-choice"
              checked={remember}
              onCheckedChange={(v) => setRemember(!!v)}
              data-testid="download-remember-checkbox"
            />
            <label htmlFor="remember-choice" className="text-sm cursor-pointer">
              Remember my choice for next time
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={downloading}
            data-testid="download-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading || loadingMeta || !format}
            data-testid="download-confirm-btn"
          >
            {downloading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
            ) : (
              <><Download className="h-4 w-4 mr-2" /> Download PDF</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
